/**
 * Per-alias preference: pause sending and calling until new or changed
 * devices are accepted in Member Security.
 *
 * Default is off (warn-only): the conversation banner remains, but this
 * client still wraps to currently attested devices.
 *
 * @module hooks/usePeerTrustPausePreference
 */

import { useSyncExternalStore, useCallback } from 'react';

export const PEER_TRUST_PAUSE_STORAGE_PREFIX = 'adieuu.peer-trust-pause.';
export const PEER_TRUST_PAUSE_CHANGE_EVENT = 'adieuu:peer-trust-pause-change';

const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((fn) => fn());
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  try {
    const EventCtor = window.Event ?? globalThis.Event;
    if (typeof EventCtor !== 'function') return;
    window.dispatchEvent(new EventCtor(PEER_TRUST_PAUSE_CHANGE_EVENT));
  } catch {
    // happy-dom and some test realms reject Event instances from another realm.
  }
}

function subscribe(callback: () => void) {
  listeners.add(callback);
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return () => listeners.delete(callback);
  }
  const onChange = () => callback();
  window.addEventListener(PEER_TRUST_PAUSE_CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    listeners.delete(callback);
    window.removeEventListener(PEER_TRUST_PAUSE_CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

function getSnapshot(identityId: string): boolean {
  if (!identityId) return false;
  try {
    return localStorage.getItem(PEER_TRUST_PAUSE_STORAGE_PREFIX + identityId) === 'on';
  } catch {
    return false;
  }
}

/** True when this alias pauses send/call until new devices are accepted. */
export function isPeerTrustPauseEnabled(identityId: string): boolean {
  return getSnapshot(identityId);
}

export function savePeerTrustPauseEnabled(identityId: string, enabled: boolean): void {
  if (!identityId) return;
  try {
    const key = PEER_TRUST_PAUSE_STORAGE_PREFIX + identityId;
    if (enabled) {
      localStorage.setItem(key, 'on');
    } else {
      localStorage.setItem(key, 'off');
    }
  } catch {
    // Storage full or unavailable
  }
  emitChange();
}

/**
 * React hook for the pause-on-new-devices preference for a given alias.
 */
export function usePeerTrustPausePreference(
  identityId: string,
): [boolean, (enabled: boolean) => void] {
  const getSnapshotForId = useCallback(() => getSnapshot(identityId), [identityId]);
  const value = useSyncExternalStore(subscribe, getSnapshotForId, () => false);

  const setValue = useCallback(
    (enabled: boolean) => savePeerTrustPauseEnabled(identityId, enabled),
    [identityId],
  );

  return [value, setValue];
}
